import { Component, OnInit } from '@angular/core';
import { DecimalPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InvoiceserviceService } from '../../../../Services/invoiceservice.service';
import { NotificationService } from '../../../../Services/notification.service';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';

@Component({
  selector: 'app-udhar-report',
  standalone: true,
  imports: [NgFor, NgClass, FormsModule, DecimalPipe, NgIf],
  templateUrl: './udhar-report.component.html',
  styleUrl: './udhar-report.component.css'
})
export class UdharReportComponent implements OnInit {

  udharList: any[] = [];
  paginatedList: any[] = [];

  currentPage = 1;
  itemsPerPage = 10;
  totalPages = 0;

  searchText = '';
  sortKey = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  totalUdhar = 0;
  totalPaid = 0;
  uniqueCustomers = 0;


  // ✅ Udhar Columns (based on your API)
  columns = [
    { key: 'invoice_number', label: 'Invoice No' },
    { key: 'invoice_date', label: 'Invoice Date' },
    { key: 'customer_name', label: 'Customer Name' },
    { key: 'customer_mob', label: 'Mobile' },
    { key: 'total_amount_before_disc', label: 'Total Amount' },
    { key: 'amount_paid', label: 'Paid Amount' },
    { key: 'balance_due', label: 'Balance (Udhar)' },
    { key: 'payment_mode', label: 'Payment Mode' },
    { key: 'status', label: 'Status' }
  ];

  constructor(
    private invoiceService: InvoiceserviceService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.loadUdharReport();
  }

  loadUdharReport() {
  this.invoiceService.getActiveUdhars().subscribe({
    next: (res: any) => {
      const rows =
        Array.isArray(res) ? res :
        Array.isArray(res?.data) ? res.data :
        Array.isArray(res?.data?.data) ? res.data.data :
        [];

      this.udharList = rows.filter(
        (i: any) => Number(i.balance_due) > 0
      );

      this.calculateSummary();
      this.filterTable();
    },
    error: () => {
      this.notificationService.showError('Failed to load Udhar report', 'Error');
    }
  });
}

calculateSummary() {
  this.totalUdhar = this.udharList.reduce(
    (sum, i) => sum + Number(i.balance_due || 0),
    0
  );

  this.totalPaid = this.udharList.reduce(
    (sum, i) => sum + Number(i.amount_paid || 0),
    0
  );

  const customerSet = new Set(
    this.udharList.map(i => i.customer_id)
  );
  this.uniqueCustomers = customerSet.size;
}

filterTable() {
  let filtered = this.udharList.filter(item => {
    const searchableText = `
      ${item.invoice_number}
      ${item.customer_name}
      ${item.customer_mob}
      ${item.balance_due}
      ${item.invoice_date}
    `.toLowerCase();

    return searchableText.includes(this.searchText.toLowerCase());
  });

  if (this.sortKey) {
    filtered.sort((a, b) => {
      const x = a[this.sortKey] ?? '';
      const y = b[this.sortKey] ?? '';
      return this.sortDirection === 'asc'
        ? x > y ? 1 : -1
        : x < y ? 1 : -1;
    });
  }

  this.totalPages = Math.ceil(filtered.length / this.itemsPerPage);
  const start = (this.currentPage - 1) * this.itemsPerPage;
  this.paginatedList = filtered.slice(start, start + this.itemsPerPage);
}


  sortBy(key: string) {
    this.sortDirection =
      this.sortKey === key && this.sortDirection === 'asc' ? 'desc' : 'asc';
    this.sortKey = key;
    this.filterTable();
  }

  goToPage(page: number) {
    this.currentPage = page;
    this.filterTable();
  }

  getValue(item: any, key: string) {
    // fallback for nested customer
    if (key === 'customer_name') {
      return item.customer_name || item.customer?.name || '-';
    }
    if (key === 'customer_mob') {
      return item.customer_mob || item.customer?.mobilenumber || '-';
    }
    return item[key] ?? '-';
  }

private getExportData(): Record<string, any>[] {
  return this.udharList.map((item, index) => ({
    'No': index + 1,
    'Invoice No': item.invoice_number,
    'Invoice Date': item.invoice_date,
    'Customer Name': item.customer_name || item.customer?.name || '',
    'Mobile': item.customer_mob || item.customer?.mobilenumber || '',
    'Total Amount': item.total_amount_before_disc,
    'Paid Amount': item.amount_paid,
    'Balance Due': item.balance_due,
    'Payment Mode': item.payment_mode || '',
    'Status': item.status
  }));
}

downloadPDF() {
  const doc = new jsPDF('l', 'mm', 'a4');

  const columns = Object.keys(this.getExportData()[0]);
  const rows = this.getExportData().map(obj => Object.values(obj));

  autoTable(doc, {
    head: [columns],
    body: rows,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [79, 70, 229], textColor: 255 }, // Indigo
  });

  doc.save('udhar-report.pdf');
}

downloadExcel() {
  const worksheet: XLSX.WorkSheet =
    XLSX.utils.json_to_sheet(this.getExportData());

  const workbook: XLSX.WorkBook = {
    Sheets: { 'Udhar Report': worksheet },
    SheetNames: ['Udhar Report']
  };

  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array'
  });

  const blob = new Blob([excelBuffer], {
    type: 'application/octet-stream'
  });

  FileSaver.saveAs(blob, 'udhar-report.xlsx');
}

downloadCSV() {
  const data = this.getExportData();
  const headers = Object.keys(data[0]);

  let csv = headers.join(',') + '\n';

  data.forEach(row => {
    csv += headers.map(h => `"${row[h]}"`).join(',') + '\n';
  });

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  FileSaver.saveAs(blob, 'udhar-report.csv');
}
downloadJSON() {
  const json = JSON.stringify(this.getExportData(), null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  FileSaver.saveAs(blob, 'udhar-report.json');
}


}
